import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import EntityTableProvider from '../entity-table-provider/entity-table-provider.vue';
import ExportModal from '@shared/components/modals/export-modal/export-modal.vue';
import {
  AccountingIntegrationSettings,
  SyncServiceType,
} from '@accounting-integrations/interfaces/accounting-integrations';

@Component({
  components: {
    'bpc-export-modal': ExportModal,
  },
})
export default class InvoiceSyncMixin extends Vue {
  declare $refs: {
    provider: InstanceType<typeof EntityTableProvider> & {
      getEntities(): Promise<void>;
      exportAll(): Promise<void>;
      exportCancel(): Promise<void>;
      downloadingExport: boolean;
    };
  };

  public showInvoiceSyncModal = false;
  public showInvoiceSyncSettingForm = false;
  public invoiceSyncEventsError = [];
  public invoiceSyncEventsSuccess = [];
  public invoiceSyncRequestSent = false;
  public isSyncingInvoices = false;
  public invoiceSyncSessionCode: string = null;
  public declare selection;
  public sessionCode: string = null;
  public generatedSyncFile: string = null;

  onInvoicesToSyncItems() {
    this.invoiceSyncEventsError = [];
    this.invoiceSyncEventsSuccess = [];
    this.invoiceSyncRequestSent = false;
    this.isSyncingInvoices = false;
    this.showInvoiceSyncSettingForm = false;
    this.invoiceSyncSessionCode = null;
    this.showInvoiceSyncModal = true;
  }

  destroyed() {
    if (this.sessionCode) {
      this.$bpcEcho.leaveInvoiceSyncChannel(this.sessionCode);
    }
  }

  get accountingIntegrationSettings(): AccountingIntegrationSettings {
    if (this.$modulesService.hasModuleActivated('accounting_integrations')) {
      return this.$store.getters['centerSettings/accountingIntegrationSettings'];
    }

    return null;
  }

  get syncServiceType(): SyncServiceType {
    return this.accountingIntegrationSettings?.sync_service_type;
  }

  onCloseInvoiceSyncConfirmModal() {
    this.showInvoiceSyncModal = false;
    this.showInvoiceSyncSettingForm = false;
  }

  onConfirmAllInvoicesToSyncItems() {
    // TODO implemente a real process ALL function
    const processAll = true;
    this.onConfirmInvoicesToSyncItems(processAll);
  }

  onConfirmInvoicesToSyncItems(processAll: boolean = false) {
    if (this.sessionCode) {
      this.$bpcEcho.leaveInvoiceSyncChannel(this.sessionCode);
    }

    const basePath = this.$bpcApiRouteHelper.getEntityPath('invoice-sync-items');
    this.sessionCode = `invoice-table-${Date.now()}`;
    this.invoiceSyncSessionCode = this.sessionCode;
    this.listenToWebsocketInvoiceSyncChannel(this.sessionCode);
    this.invoiceSyncRequestSent = true;

    this.$bpcApi
      .post(basePath + '/from-invoices', {
        invoices: this.selection,
        session_code: this.sessionCode,
        process_all: processAll,
      })
      .subscribe(
        res => {
          this.$refs.provider.getEntities();
          // this.showInvoiceSyncModal = false;
          this.invoiceSyncRequestSent = true;
        },
        error => {
          this.manageInvoiceSyncError(error);
        },
      );
  }

  async onDownloadSyncFile() {
    // TODO It is probably a better idea to add a new endpoint to directly download the zip file that was already generated during the POST call.
    // That endpoint would also return a download response, just as this one, but serving the already existing file
    const basePath = this.$bpcApiRouteHelper.getEntityPath('invoice-sync-items');
    const invoiceSyncItems = this.invoiceSyncEventsSuccess.map(item => item.invoiceSyncItem.id);
    if (invoiceSyncItems.length) {
      const fileName = `invoice-sync-${Date.now()}.zip`;
      this.$bpcApi
        .postDownload(`${basePath}/bulk-download`, fileName, {
          invoice_sync_item_ids: invoiceSyncItems,
        })
        .subscribe(
          res => {
            console.log(res);
          },
          error => {
            this.$bpcMessage.showErrorMessage({
              text: 'global.invoice_sync_item.bulk_download.error',
            });
          },
        );
    } else {
      // TODO display modal error
    }
  }

  listenToWebsocketInvoiceSyncChannel(sessionCode) {
    this.isSyncingInvoices = true;

    this.$bpcEcho
      .getInvoiceSyncChannel(sessionCode)
      .listen('.BPC\\AccountingIntegrations\\Events\\InvoiceSyncFileGenerated', data => {
        // console.log('InvoiceSyncFileGenerated', data);
        this.generatedSyncFile = data?.fileUrl;
        // this.downloadInvoiceSyncItemGeneratedFile(data);
      })
      .listen('.BPC\\AccountingIntegrations\\Events\\InvoiceSyncItemBulkProcessed', data => {
        // console.log('InvoiceSyncItemBulkProcessed', data);
        this.$bpcMessage.showInfoMessage({
          text: 'global.accounting.invoices.all_invoices_synced_reload_page',
        });
        this.isSyncingInvoices = false;
        this.$refs.provider.getEntities();
        // this.$bvModal.hide('modal-generate-invoices');
      })
      .listen('.BPC\\AccountingIntegrations\\Events\\InvoiceSyncItemProcessed', data => {
        // console.log('InvoiceSyncItemProcessed', data);
        this.invoiceSyncEventsSuccess.push(data);
      })
      .listen('.BPC\\AccountingIntegrations\\Events\\InvoiceSyncItemErrorProcessing', data => {
        // console.log('InvoiceSyncItemErrorProcessing', data);
        this.invoiceSyncEventsError.push(data);
      })
      .listen('.BPC\\AccountingIntegrations\\Events\\ExactConfigSaved', data => {
        // console.log('ExactConfigSaved', data);
        this.$bpcMessage.showInfoMessage({
          text: 'global.invoice_sync_items.exact_config_saved',
        });
        this.showInvoiceSyncSettingForm = false;
      });
  }

  downloadInvoiceSyncItemGeneratedFile(data) {
    // console.log('downloadInvoiceSyncItemGeneratedFile', data);
    const downloadUrl = data?.fileUrl;
    window.location.href = downloadUrl;
  }

  manageInvoiceSyncError(error) {
    this.invoiceSyncRequestSent = true;
    this.isSyncingInvoices = false;
    const exceptionType = error?.response?.data?.exception;
    const message = error?.response?.data?.message;
    switch (exceptionType) {
      case 'BPC\\AccountingIntegrations\\Exceptions\\TenantNotReadyToSyncInvoicesException':
        this.$bpcMessage.showErrorMessage({ text: message });
        this.showCenterInvoiceSyncSettingForm();
        break;
      case 'BPC\\AccountingIntegrations\\Exceptions\\SyncFilesNotFoundException':
        this.$bpcMessage.showErrorMessage({ text: message });
        break;

      default:
        this.$bpcMessage.showErrorMessage({
          text: 'global.invoices_to_sync_item.error',
        });
        break;
    }
  }

  showCenterInvoiceSyncSettingForm() {
    this.showInvoiceSyncSettingForm = true;
  }
}
